import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi } from './useApi';

const SCAN_STATUS_POLL_INTERVAL_MS = 5_000;

export interface ScanProgress {
  status: 'idle' | 'scanning' | 'clean' | 'malicious' | 'error';
  runId: string | null;
  maliciousFiles: string[];
  errorMessage?: string | null;
}

const IDLE_PROGRESS: ScanProgress = { status: 'idle', runId: null, maliciousFiles: [] };

/**
 * Polls GET /storage/meter-data/scan-status/:batchId until the malware scan for
 * every file in the batch resolves — Clean (with the ADF RunId to hand off to
 * useUploadProgress) or Malicious (with the offending file names).
 *
 * The backend marks a batch Clean before it finishes triggering the ADF pipeline
 * and writing back the RunId, so a Clean response may briefly arrive with no
 * runId yet — keep polling in that case rather than stopping early, otherwise
 * the caller never learns the RunId and the progress UI never appears.
 */
export function useScanStatusPoll(batchId: string | null) {
  const [scanProgress, setScanProgress] = useState<ScanProgress>(IDLE_PROGRESS);
  const { checkScanStatus } = useApi();
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const resetScan = useCallback(() => {
    stopPolling();
    setScanProgress(IDLE_PROGRESS);
  }, [stopPolling]);

  useEffect(() => {
    if (!batchId) return undefined;

    setScanProgress({ status: 'scanning', runId: null, maliciousFiles: [] });

    const poll = async () => {
      try {
        const response = await checkScanStatus(batchId);
        const data = response?.data;
        if (!data) return;

        if (data.status === 'Clean' && data.runId) {
          setScanProgress({ status: 'clean', runId: data.runId, maliciousFiles: [] });
          stopPolling();
        } else if (data.status === 'Malicious') {
          setScanProgress({ status: 'malicious', runId: null, maliciousFiles: data.maliciousFiles ?? [] });
          stopPolling();
        } else if (data.status === 'PipelineTriggerFailed') {
          setScanProgress({
            status: 'error',
            runId: null,
            maliciousFiles: [],
            errorMessage: data.errorMessage ?? null,
          });
          stopPolling();
        }
        // PendingScan, or Clean without a runId yet (ADF trigger still in flight): keep polling.
      } catch {
        // Transient failure — keep polling until the interval retries.
      }
    };

    poll();
    pollRef.current = setInterval(poll, SCAN_STATUS_POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [batchId, checkScanStatus, stopPolling]);

  return { scanProgress, resetScan };
}
